import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

// MUI
import { Box, InputLabel, TextField } from '@mui/material';

// Shared components
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';

// Local
import OLCBaseModal from '../../BaseModal';
import DatePicker from '~/components/General/OlcDatePicker';
import { MultiSelectArrowCIcon } from '~/assets/images';
import { fetchAdminPlansOptions } from '~/redux/actions/admin-pricing-actions';
import { failure } from '~/redux/actions/snackbar-actions';

// Styles
import './styles.scss';

const UpdatePlanModal = ({ open, handleClose, formData, setFormData, onSave }) => {
  const dispatch = useDispatch();
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [changeTypeOptions, setChangeTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  // Fetch change types from options API when modal opens
  useEffect(() => {
    if (open) {
      fetchChangeTypes();
    }
  }, [open]);

  // Reset form data when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({
        effectiveDate: null,
        changeType: null,
        notes: '',
      });
      setIsDatepickerOpen(false);
    }
  }, [open, setFormData]);

  const fetchChangeTypes = async () => {
    try {
      setLoading(true);
      const response = await dispatch(fetchAdminPlansOptions());
      if (response.status === 200) {
        const { data } = response.data;

        // Map change_types for dropdown
        if (data.change_types && Array.isArray(data.change_types)) {
          const mappedChangeTypes = data.change_types.map((changeType) => ({
            id: changeType.id,
            title: changeType.label,
          }));
          setChangeTypeOptions(mappedChangeTypes);
        }
      }
    } catch (error) {
      dispatch(failure('Failed to load change types'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key) => (event) => {
    setFormData((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, effectiveDate: date }));
  };

  const handleChangeTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, changeType: value }));
  };

  const { notes, changeType, effectiveDate } = formData || {
    effectiveDate: null,
    changeType: null,
    notes: '',
  };

  // Disable save button if effective date or change type is not selected
  const isSaveDisabled = !effectiveDate || !changeType;

  const handleSave = async () => {
    // Validate required fields
    if (!effectiveDate) {
      dispatch(failure('Please select an effective date'));
      return;
    }

    if (!changeType) {
      dispatch(failure('Please select a change type'));
      return;
    }

    if (onSave) {
      await onSave();
    } else {
      handleClose();
    }
  };

  return (
    <OLCBaseModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSave}
      title="Update Plan"
      subTitle="You’re about to update the plan for this account. Once saved, these changes will take effect on the Effective Date you’ve selected."
      primaryText="Save"
      secondaryText="Cancel"
      width={640}
      paperClassName="updatePlanModalWrapper"
      disableScrollLock={false}
      disablePrimary={isSaveDisabled}
    >
      <Box className="updatePlanModalContent">
        <Box className="mailedDate newDatePicker updatedDesign">
          <Box className="selectDate">
            <InputLabel className="dateLabel newfieldsLabel">Effective Date*</InputLabel>
            <img
              src={MultiSelectArrowCIcon}
              alt="arrow"
              style={{ transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
            <DatePicker
              minDate={tomorrow}
              selected={effectiveDate}
              onCalendarClose={() => setIsDatepickerOpen(false)}
              onChange={handleDateChange}
              onFocus={() => setIsDatepickerOpen(true)}
              onBlur={() => setIsDatepickerOpen(false)}
              placeholderText="Select date"
              isClearable
              dateFormat="MM/dd/yyyy"
            />
          </Box>
        </Box>

        <Box className="row">
          <InputLabel className="fieldLabel">Change Type*</InputLabel>
          <Box className="changeTypeSelect">
            <OlcDropdownSearch
              options={changeTypeOptions}
              selectedValue={changeType}
              setSelectedValue={handleChangeTypeChange}
              placeHolderText="Select change type"
              searchFieldPlaceholderText="Search change type"
              clearable
            />
          </Box>
        </Box>

        <Box className="row">
          <InputLabel className="fieldLabel">Notes</InputLabel>
          <TextField
            className="notesInput"
            multiline
            minRows={4}
            placeholder="Add any notes..."
            value={notes.trimStart()}
            onChange={handleChange('notes')}
          />
        </Box>
      </Box>
    </OLCBaseModal>
  );
};

export default UpdatePlanModal;
